import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Brain, Target, Rocket, Zap, X } from 'lucide-react';

/**
 * Beautiful loading animation for career path and roadmap generation
 * Uses animated orbs, particles, and engaging messages
 * Enhanced with estimated time, progress percentage, and optional cancel button
 */
const LoadingSpinner = ({
  message = "Analyzing your profile and generating insights...",
  variant = "default", // "default", "career", "roadmap"
  onCancel = null, // Pass a function to show a cancel button
  estimatedSeconds = 15, // Estimated total time in seconds
}) => {
  const messages = {
    default: [
      "Processing your request...",
      "Analyzing data patterns...",
      "Almost there..."
    ],
    career: [
      "🧠 Analyzing your unique skillset...",
      "🎯 Mapping career opportunities...",
      "✨ Generating personalized insights...",
      "🚀 Discovering your ideal paths..."
    ],
    roadmap: [
      "📊 Building your learning curriculum...",
      "🗓️ Optimizing your timeline...",
      "📚 Curating resources...",
      "🎓 Finalizing your roadmap..."
    ]
  };

  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0);
  const [elapsedSeconds, setElapsedSeconds] = React.useState(0);
  const variantMessages = messages[variant] || messages.default;

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex(prev => (prev + 1) % variantMessages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [variantMessages.length]);

  // Timer for elapsed time and progress
  React.useEffect(() => {
    const timer = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate progress percentage (capped at 95% to avoid showing 100% before done)
  const progressPercent = Math.min(95, Math.round((elapsedSeconds / estimatedSeconds) * 100));
  const remainingSeconds = Math.max(0, estimatedSeconds - elapsedSeconds);

  return (
    <div className="fixed inset-0 bg-[#08090c]/95 backdrop-blur-xl flex items-center justify-center z-50">
      {/* Background grid pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(16, 185, 129, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(16, 185, 129, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
            scale: [1, 0.9, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center max-w-md mx-4">
        {/* Animated icon cluster */}
        <div className="relative w-32 h-32 mb-8">
          {/* Central pulsing orb */}
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full opacity-20"
          />
          <motion.div
            animate={{ scale: [1.1, 1, 1.1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
            className="absolute inset-4 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full opacity-30"
          />

          {/* Central icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              {variant === 'career' ? (
                <Brain className="w-12 h-12 text-emerald-400" />
              ) : variant === 'roadmap' ? (
                <Target className="w-12 h-12 text-emerald-400" />
              ) : (
                <Sparkles className="w-12 h-12 text-emerald-400" />
              )}
            </motion.div>
          </div>

          {/* Orbiting icons */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0"
          >
            <div className="absolute -top-2 left-1/2 -translate-x-1/2">
              <div className="w-8 h-8 bg-[#0F131F] border border-emerald-500/30 rounded-lg flex items-center justify-center">
                <Rocket className="w-4 h-4 text-emerald-400" />
              </div>
            </div>
          </motion.div>
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0"
          >
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
              <div className="w-8 h-8 bg-[#0F131F] border border-cyan-500/30 rounded-lg flex items-center justify-center">
                <Zap className="w-4 h-4 text-cyan-400" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-white font-mono mb-3 text-center"
        >
          {variant === 'career' ? 'DISCOVERING CAREERS' :
            variant === 'roadmap' ? 'BUILDING ROADMAP' :
              'PROCESSING'}
        </motion.h2>

        {/* Animated message */}
        <div className="h-8 flex items-center justify-center mb-6">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentMessageIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-slate-400 font-mono text-sm text-center"
            >
              {variantMessages[currentMessageIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Progress bar with percentage */}
        <div className="w-72 mb-3">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Progress</span>
            <span className="text-[10px] font-mono text-emerald-400 tabular-nums">{progressPercent}%</span>
          </div>
          <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-500"
              initial={{ width: '0%' }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Estimated time remaining */}
        <p className="text-slate-600 text-xs font-mono tabular-nums mb-4">
          {remainingSeconds > 0
            ? `~${remainingSeconds}s remaining`
            : 'Finishing up...'
          }
        </p>

        {/* Particle effects */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -100, 0],
                x: [0, (i % 2 === 0 ? 20 : -20), 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.5,
              }}
              className="absolute w-1 h-1 bg-emerald-400 rounded-full"
              style={{
                left: `${20 + i * 12}%`,
                top: '60%',
              }}
            />
          ))}
        </div>

        {/* Cancel button (optional) */}
        {onCancel && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3 }}
            onClick={onCancel}
            className="flex items-center gap-2 px-4 py-2 mt-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-white transition-all font-mono text-xs uppercase tracking-wider"
          >
            <X className="w-3 h-3" />
            Cancel
          </motion.button>
        )}
      </div>
    </div>
  );
};

export default LoadingSpinner;